import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Package, Factory as FactoryIcon, CheckCircle2, Truck, Clock, AlertTriangle, X } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const money = (n) => (n || n === 0 ? Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—");

// Operational status badges
export const ORDER_STATUS = {
  awaiting_advance: { label: "Awaiting Advance", cls: "bg-amber-100 text-amber-700" },
  in_production: { label: "In Production", cls: "bg-sky-100 text-sky-700" },
  ready_for_dispatch: { label: "Ready for Dispatch", cls: "bg-violet-100 text-violet-700" },
  dispatched: { label: "Dispatched", cls: "bg-emerald-100 text-emerald-700" },
};

export const StatusBadge = ({ status }) => {
  const m = ORDER_STATUS[status] || { label: status || "—", cls: "bg-secondary text-muted-foreground" };
  return <span data-testid={`status-badge-${status}`} className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${m.cls}`}>{m.label}</span>;
};

// Traffic-light tone for a due date. Green=on track, Yellow=due soon, Red=overdue.
export const DUE_SOON_DAYS = 14;
export const dateTone = (dateStr, done, dispatched) => {
  if (!dateStr) return "text-muted-foreground";
  if (done || dispatched) return "text-emerald-600";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + "T00:00:00");
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0) return "text-red-600 font-semibold";
  if (diff <= DUE_SOON_DAYS) return "text-amber-600 font-semibold";
  return "text-emerald-600";
};

const StatCard = ({ label, value, tone, icon: Icon, testid, active, onClick }) => (
  <button type="button" data-testid={testid} onClick={onClick} aria-pressed={active}
    className={`text-left rounded-lg border p-3 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-md ${tone} ${active ? "ring-2 ring-offset-2 ring-foreground shadow-md" : ""}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium opacity-80">{label}</span>
      {Icon && <Icon className="h-4 w-4 opacity-70" />}
    </div>
    <div className="font-display text-2xl font-bold mt-1">{value}</div>
  </button>
);

const CARD_LABELS = {
  active: "Active Orders", awaiting_advance: "Awaiting Advance", customer_due_soon: "Customer Due Soon",
  customer_overdue: "Customer Overdue", factory_due_soon: "Factory Due Soon", factory_overdue: "Factory Overdue",
  ready_for_dispatch: "Ready for Dispatch", dispatched: "Dispatched",
};

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [factory, setFactory] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("updated");
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState("");
  const toggleCard = (k) => setCard((c) => (c === k ? "" : k));

  useEffect(() => { api.get("/factories").then((r) => setFactories(r.data)).catch(() => {}); }, []);
  const loadStats = useCallback(() => api.get("/orders/dashboard").then((r) => setStats(r.data)).catch(() => {}), []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (factory) params.factory = factory;
    try { const r = await api.get("/orders", { params }); setOrders(r.data); }
    finally { setLoading(false); }
  }, [search, factory]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const view = useMemo(() => {
    let list = status ? orders.filter((o) => o.operational_status === status) : orders.slice();
    if (card && stats?.ids?.[card]) { const set = new Set(stats.ids[card]); list = list.filter((o) => set.has(o.id)); }
    const cmp = {
      customer_delivery: (a, b) => (a.customer_expected_delivery_date || "9999") .localeCompare(b.customer_expected_delivery_date || "9999"),
      factory_completion: (a, b) => (a.factory_expected_completion_date || "9999").localeCompare(b.factory_expected_completion_date || "9999"),
      pi_value: (a, b) => (b.total_pi_value || 0) - (a.total_pi_value || 0),
      updated: () => 0,
    }[sort];
    if (cmp) list.sort(cmp);
    return list;
  }, [orders, status, sort, card, stats]);

  return (
    <div className="p-8 max-w-[1400px] animate-in-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Order Book</h1>
          <p className="text-muted-foreground mt-1">{orders.length} order{orders.length !== 1 ? "s" : ""}</p>
        </div>
        <Button data-testid="new-order-button" onClick={() => navigate("/orders/new")} className="gap-2 font-semibold"><Plus className="h-4 w-4" /> New Order</Button>
      </div>

      {/* Operational dashboard */}
      {stats && (
        <div data-testid="order-dashboard" className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-3">
          <StatCard testid="stat-active" label="Active Orders" value={stats.active} tone="border-border bg-white" icon={Package} active={card === "active"} onClick={() => toggleCard("active")} />
          <StatCard testid="stat-awaiting" label="Awaiting Advance" value={stats.awaiting_advance} tone="border-amber-200 bg-amber-50 text-amber-800" icon={Clock} active={card === "awaiting_advance"} onClick={() => toggleCard("awaiting_advance")} />
          <StatCard testid="stat-cust-due" label={`Cust. Due ≤${stats.due_soon_days}d`} value={stats.customer_due_soon} tone="border-amber-200 bg-amber-50 text-amber-800" icon={Clock} active={card === "customer_due_soon"} onClick={() => toggleCard("customer_due_soon")} />
          <StatCard testid="stat-cust-overdue" label="Cust. Overdue" value={stats.customer_overdue} tone="border-red-200 bg-red-50 text-red-700" icon={AlertTriangle} active={card === "customer_overdue"} onClick={() => toggleCard("customer_overdue")} />
          <StatCard testid="stat-fac-due" label={`Factory Due ≤${stats.due_soon_days}d`} value={stats.factory_due_soon} tone="border-amber-200 bg-amber-50 text-amber-800" icon={Clock} active={card === "factory_due_soon"} onClick={() => toggleCard("factory_due_soon")} />
          <StatCard testid="stat-fac-overdue" label="Factory Overdue" value={stats.factory_overdue} tone="border-red-200 bg-red-50 text-red-700" icon={AlertTriangle} active={card === "factory_overdue"} onClick={() => toggleCard("factory_overdue")} />
          <StatCard testid="stat-ready" label="Ready for Dispatch" value={stats.ready_for_dispatch} tone="border-violet-200 bg-violet-50 text-violet-700" icon={CheckCircle2} active={card === "ready_for_dispatch"} onClick={() => toggleCard("ready_for_dispatch")} />
          <StatCard testid="stat-dispatched" label="Dispatched" value={stats.dispatched} tone="border-emerald-200 bg-emerald-50 text-emerald-700" icon={Truck} active={card === "dispatched"} onClick={() => toggleCard("dispatched")} />
        </div>
      )}
      <div className="mb-5 min-h-[28px] flex items-center gap-2 text-sm">
        {card ? (
          <>
            <span data-testid="card-filter-label" className="text-muted-foreground">Showing: <span className="font-semibold text-foreground">{CARD_LABELS[card]}</span> ({view.length})</span>
            <button type="button" data-testid="clear-card-filter" onClick={() => setCard("")} className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-xs font-medium hover:bg-secondary"><X className="h-3 w-3" /> Clear filter</button>
          </>
        ) : <span className="text-xs text-muted-foreground">Click a card above to filter the list.</span>}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="order-search-input" placeholder="Search PI, consignee, brand, country, machine…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <select data-testid="order-factory-filter" value={factory} onChange={(e) => setFactory(e.target.value)} className="h-10 rounded-md border border-border bg-white px-3 text-sm">
          <option value="">All factories</option>
          {factories.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select data-testid="order-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-border bg-white px-3 text-sm">
          <option value="">All statuses</option>
          {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select data-testid="order-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="h-10 rounded-md border border-border bg-white px-3 text-sm">
          <option value="updated">Recently updated</option>
          <option value="customer_delivery">Customer delivery date</option>
          <option value="factory_completion">Factory completion date</option>
          <option value="pi_value">PI value (high→low)</option>
        </select>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        {loading ? <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        : view.length === 0 ? (
          <div className="p-16 text-center"><Package className="h-8 w-8 mx-auto text-muted-foreground/50 mb-3" /><p className="font-medium">No orders</p><p className="text-sm text-muted-foreground mt-1">Adjust filters or create a new PI order.</p></div>
        ) : (
          <table className="w-full text-sm min-w-[1000px]">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="px-4 py-2.5 font-medium">PI Number</th><th className="px-3 py-2.5 font-medium">Consignee</th>
              <th className="px-3 py-2.5 font-medium">Country</th>
              <th className="px-3 py-2.5 font-medium text-right">PI Value</th><th className="px-3 py-2.5 font-medium">Cust. Balance</th>
              <th className="px-3 py-2.5 font-medium">Cust. Delivery</th><th className="px-3 py-2.5 font-medium">Factory Completion</th>
              <th className="px-3 py-2.5 font-medium">Status</th><th className="px-3 py-2.5 font-medium">Factories</th>
            </tr></thead>
            <tbody className="divide-y divide-border">
              {view.map((o) => {
                const dispatched = !!o.actual_dispatch_date;
                return (
                <tr key={o.id} data-testid={`order-row-${o.id}`} onClick={() => navigate(`/orders/${o.id}`)} className="hover:bg-secondary/50 cursor-pointer">
                  <td className="px-4 py-3 font-semibold">
                    <span className="inline-flex items-center gap-1.5">{dispatched && <CheckCircle2 data-testid={`dispatched-tick-${o.id}`} className="h-4 w-4 text-emerald-600" />}{o.pi_number}</span>
                  </td>
                  <td className="px-3 py-3">{o.consignee || "—"}<div className="text-xs text-muted-foreground">{[o.customer_code, o.brand].filter(Boolean).join(" · ")}</div></td>
                  <td className="px-3 py-3 text-muted-foreground">{o.country || "—"}</td>
                  <td className="px-3 py-3 text-right font-medium">{money(o.total_pi_value)}</td>
                  <td className="px-3 py-3">
                    {o.customer_balance < 0
                      ? <span className="text-xs font-semibold text-sky-700">Over-paid {money(-o.customer_balance)}</span>
                      : <span className={`text-xs font-semibold ${o.customer_balance > 0 ? "text-amber-700" : "text-emerald-700"}`}>{money(o.customer_balance)}</span>}
                    <div className="text-[10px] text-muted-foreground">{Math.min(100, o.customer_paid_pct)}% paid{o.customer_balance < 0 ? " +" : ""}</div>
                  </td>
                  <td className={`px-3 py-3 text-xs ${dateTone(o.customer_expected_delivery_date, false, dispatched)}`}>{o.customer_expected_delivery_date || "—"}</td>
                  <td className={`px-3 py-3 text-xs ${dateTone(o.factory_expected_completion_date, false, dispatched)}`}>{o.factory_expected_completion_date || "—"}</td>
                  <td className="px-3 py-3"><StatusBadge status={o.operational_status} /></td>
                  <td className="px-3 py-3 text-muted-foreground text-xs">
                    <span className="inline-flex items-center gap-1"><FactoryIcon className="h-3 w-3" />{[...new Set((o.lines || []).map((l) => l.factory).filter(Boolean))].join(", ") || "—"}</span>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
